import { useEffect, useRef, useState } from "react";
import { useTablePreferences } from "../../context/tablePreferencesProvider";
import { mergeLayout, toSavedLayout } from "./tableLayout";

export const SAVE_DEBOUNCE_MS = 600;

/**
 * Estado de ordem/visibilidade/largura de uma tabela.
 * Carrega o salvo; enquanto o usuário não mexer, acompanha o que chegar do servidor;
 * depois de mexer, salva com debounce (e no desmonte, se houver pendência).
 */
export const useTableLayout = (tableKey, columns) => {
    const preferences = useTablePreferences();
    const saved = preferences?.layouts?.[tableKey];
    const savedJson = JSON.stringify(saved ?? null);
    const columnsRef = useRef(columns);
    columnsRef.current = columns;
    const preferencesRef = useRef(preferences);
    preferencesRef.current = preferences;
    const [layout, setLayout] = useState(() => mergeLayout(columns, saved));
    const touchedRef = useRef(false);
    const pendingRef = useRef(null);

    useEffect(() => {
        if (!touchedRef.current) setLayout(mergeLayout(columnsRef.current, saved));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [savedJson]);

    useEffect(() => {
        if (!touchedRef.current || !preferencesRef.current || !tableKey) return undefined;
        pendingRef.current = toSavedLayout(columnsRef.current, layout);
        const timer = setTimeout(() => {
            preferencesRef.current?.saveLayout(tableKey, pendingRef.current);
            pendingRef.current = null;
        }, SAVE_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [layout, tableKey]);

    useEffect(() => () => {
        if (pendingRef.current && preferencesRef.current && tableKey) {
            preferencesRef.current.saveLayout(tableKey, pendingRef.current);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setter = (field) => (updater) => {
        touchedRef.current = true;
        setLayout((current) => ({
            ...current,
            [field]: typeof updater === "function" ? updater(current[field]) : updater,
        }));
    };

    const resetLayout = () => {
        touchedRef.current = false;
        pendingRef.current = null;
        setLayout(mergeLayout(columnsRef.current, null));
        preferencesRef.current?.resetLayout(tableKey);
    };

    return {
        layout,
        onColumnOrderChange: setter("columnOrder"),
        onColumnVisibilityChange: setter("columnVisibility"),
        onColumnSizingChange: setter("columnSizing"),
        resetLayout,
    };
};
